import NumberField from '@components/Customization/NumberField';
import {
  OPERATOR_VALUE_KIND, operatorsFor, STEP_STATUS_ID, type RuleSubjectType, type RuleValueKind,
} from '@lib/customization/poiRules';
import { useLocalization } from '@lib/localization';
import {
  Alert, Box, Button, Chip, FormControl, IconButton, InputLabel, ListSubheader, MenuItem, OutlinedInput,
  Select, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  conditionRowStyles, dangerHoverStyles, dashedButtonStyles, ruleCardStyles,
} from '@pages/CustomPoiTypes.styles';
import {
  newEntityId, ruleTargets, type PoiFieldCondition, type PoiFieldOption,
  type PoiFieldRule, type RuleOperator, type RuleTarget,
} from '@store/api/poiTypeEndpoints';
import { PlusIcon, TrashIcon } from 'react-line-awesome';

/** How many rules and conditions the API accepts; mirrored so the buttons stop in time. */
const MAX_RULES = 10;
const MAX_CONDITIONS = 10;

/**
 * What a condition may point at, flattened for the picker: everything the editor needs
 * to render an operator list and a value input, plus where it came from so the list can be
 * grouped by type the way the schema table is. A field, or the step's status, which stands
 * in the same list with its four names as its choices (`stepStatusCandidate`).
 */
export interface RuleCandidate {
  id: string;
  /** Already resolved through `resolveLabel` — this component does no locale work. */
  label: string;
  type: RuleSubjectType;
  options?: PoiFieldOption[];
  decimal?: boolean;
  min?: number;
  max?: number;
  /**
   * The ancestor this field is defined on, or null when it belongs to the type being edited.
   * The step's status has a heading of its own, since no type defines it.
   */
  source: string | null;
  groupName?: string;
}

interface Props {
  rules: PoiFieldRule[];
  onChange: (rules: PoiFieldRule[]) => void;
  candidates: RuleCandidate[];
  /** The entry being edited, excluded from the picker. */
  selfId?: string;
  /** Which properties a rule may set. Groups pass `['visible']`. */
  targets?: readonly RuleTarget[];
  baseVisible: boolean;
  onBaseVisibleChange: (visible: boolean) => void;
  disabled?: boolean;
}

type ThreeState = 'leave' | 'on' | 'off';

const stateOf = (then: PoiFieldRule['then'], target: RuleTarget): ThreeState => {
  const value = then[target];

  return value === undefined ? 'leave' : (value ? 'on' : 'off');
};

/**
 * What a condition should hold when its operator changes.
 *
 * Kept only when the new operator wants the same kind of value — switching `equals` to
 * `notEquals` should not make the author pick the option again, but switching it to
 * `countGt` must not leave an option id sitting in a numeric box.
 */
const valueFor = (
  previous: PoiFieldCondition, operator: RuleOperator, candidate?: RuleCandidate
): PoiFieldCondition['value'] => {
  const before = OPERATOR_VALUE_KIND[previous.operator];
  const after = OPERATOR_VALUE_KIND[operator];

  if (after === 'none') return undefined;
  if (before === after) return previous.value;
  if (after === 'count') return 0;
  if (after === 'number') return candidate?.min ?? 0;
  if (after === 'list') return [];
  if (after === 'text') return '';

  // `single` follows the referenced field's own shape.
  switch (candidate?.type) {
    case 'Toggle': return true;
    case 'Number':
    case 'Range': return candidate.min ?? 0;
    case 'MultiList': return [];
    default: return '';
  }
};

const RuleEditor: React.FC<Props> = ({
  rules, onChange, candidates, selfId, targets = ruleTargets, baseVisible, onBaseVisibleChange, disabled,
}) => {
  const { t } = useLocalization();

  // A field cannot usefully gate itself: "show me when I equal X" hides it before anyone
  // can answer it, which reads as a bug rather than as a rule.
  const pickable = candidates.filter((candidate) => candidate.id !== selfId);
  const byId = new Map(pickable.map((candidate) => [candidate.id, candidate]));

  const setRule = (index: number, rule: PoiFieldRule) =>
    onChange(rules.map((current, position) => (position === index ? rule : current)));

  const newCondition = (): PoiFieldCondition => {
    const candidate = pickable[0];
    const operator = candidate ? operatorsFor(candidate.type)[0] : 'equals';

    return {
      fieldId: candidate?.id ?? '',
      operator,
      value: candidate
        ? valueFor({ fieldId: '', operator: 'isEmpty' }, operator, candidate)
        : undefined,
    };
  };

  const addRule = (visible: boolean) => onChange([...rules, {
    id: newEntityId(),
    when: { match: 'all', conditions: [newCondition()] },
    then: { visible },
  }]);

  const setCondition = (ruleIndex: number, index: number, condition: PoiFieldCondition) => {
    const rule = rules[ruleIndex];

    setRule(ruleIndex, {
      ...rule,
      when: {
        ...rule.when,
        conditions: rule.when.conditions.map((current, position) => (position === index ? condition : current)),
      },
    });
  };

  if (!pickable.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('Customization_RulesNeedFields')}
      </Typography>
    );
  }

  const valueEditor = (
    ruleIndex: number, index: number, condition: PoiFieldCondition, candidate: RuleCandidate | undefined
  ) => {
    const kind: RuleValueKind = OPERATOR_VALUE_KIND[condition.operator];
    const set = (value: PoiFieldCondition['value']) =>
      setCondition(ruleIndex, index, { ...condition, value });

    if (kind === 'none' || !candidate) return null;

    const shared = { size: 'small' as const, disabled, sx: { flex: '1 1 140px', minWidth: 140 } };
    const choices = candidate.options ?? [];

    if (kind === 'count') {
      return (
        <NumberField
          {...shared}
          label={t('Customization_ConditionValue')}
          value={typeof condition.value === 'number' ? condition.value : undefined}
          allowDecimal={false}
          allowNegative={false}
          // An empty box stays empty, which keeps Save disabled until a count is entered.
          // Defaulting it to 0 saved a rule the author had not finished writing.
          onChange={(next) => set(next)}
        />
      );
    }

    if (kind === 'number' || (kind === 'single' && (candidate.type === 'Number' || candidate.type === 'Range'))) {
      return (
        <NumberField
          {...shared}
          label={t('Customization_ConditionValue')}
          value={typeof condition.value === 'number' ? condition.value : undefined}
          allowDecimal={candidate.decimal}
          onChange={(next) => set(next)}
        />
      );
    }

    if (kind === 'single' && candidate.type === 'Toggle') {
      return (
        <TextField
          {...shared}
          select
          label={t('Customization_ConditionValue')}
          value={condition.value === false ? 'off' : 'on'}
          onChange={(event) => set(event.target.value === 'on')}
        >
          <MenuItem value="on">{t('Customization_RuleOn')}</MenuItem>
          <MenuItem value="off">{t('Customization_RuleOff')}</MenuItem>
        </TextField>
      );
    }

    // One option id: `equals` on a single-choice list.
    if (kind === 'single' && candidate.type === 'List') {
      return (
        <TextField
          {...shared}
          select
          label={t('Customization_ConditionValue')}
          value={typeof condition.value === 'string' ? condition.value : ''}
          onChange={(event) => set(event.target.value)}
        >
          {choices.map((option, position) => (
            <MenuItem key={option.id ?? position} value={option.id ?? ''}>
              {labelOfOption(option, position)}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    // A set of option ids: `isAnyOf`/`isNoneOf` on either choice type, and `equals` on a
    // multi-select.
    if (kind === 'list' || (kind === 'single' && candidate.type === 'MultiList')) {
      const selected = Array.isArray(condition.value) ? condition.value : [];

      return (
        <FormControl size="small" disabled={disabled} sx={{ flex: '1 1 180px', minWidth: 180 }}>
          <InputLabel>{t('Customization_ConditionValue')}</InputLabel>
          <Select
            multiple
            value={selected}
            input={<OutlinedInput label={t('Customization_ConditionValue')} />}
            onChange={(event) => set(
              typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value
            )}
            renderValue={(ids) => (
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {ids.map((id) => (
                  <Chip key={id} size="small" label={nameOfOption(choices, id)} />
                ))}
              </Stack>
            )}
          >
            {choices.map((option, position) => (
              <MenuItem key={option.id ?? position} value={option.id ?? ''}>
                {labelOfOption(option, position)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        {...shared}
        label={t('Customization_ConditionValue')}
        value={typeof condition.value === 'string' ? condition.value : ''}
        onChange={(event) => set(event.target.value)}
      />
    );
  };

  const conditionRow = (ruleIndex: number, condition: PoiFieldCondition, index: number) => {
    const candidate = byId.get(condition.fieldId);
    const rule = rules[ruleIndex];

    const removeCondition = () => setRule(ruleIndex, {
      ...rule,
      when: { ...rule.when, conditions: rule.when.conditions.filter((_, position) => position !== index) },
    });

    const changeField = (fieldId: string) => {
      const next = byId.get(fieldId);
      const operators = next ? operatorsFor(next.type) : [];
      const operator = operators.includes(condition.operator) ? condition.operator : operators[0];
      // A choice belongs to what it was picked on. Carried over to another field, or to the
      // step's status, its id names nothing there, and the save prunes the condition unsaid.
      const previous: PoiFieldCondition = candidate?.options?.length
        ? { fieldId: condition.fieldId, operator: 'isEmpty' }
        : condition;

      setCondition(ruleIndex, index, {
        fieldId, operator, value: valueFor(previous, operator, next),
      });
    };

    return (
      <Stack key={index} direction="row" sx={conditionRowStyles}>
        <TextField
          select
          size="small"
          disabled={disabled}
          label={t('Customization_ConditionField')}
          value={candidate ? condition.fieldId : ''}
          sx={{ flex: '2 1 180px', minWidth: 180 }}
          onChange={(event) => changeField(event.target.value)}
        >
          {pickerItems(pickable, t('Customization_OwnFields'))}
        </TextField>

        <TextField
          select
          size="small"
          disabled={disabled || !candidate}
          label={t('Customization_ConditionOperator')}
          value={candidate ? condition.operator : ''}
          sx={{ flex: '1 1 150px', minWidth: 150 }}
          onChange={(event) => {
            const operator = event.target.value as RuleOperator;

            setCondition(ruleIndex, index, {
              ...condition, operator, value: valueFor(condition, operator, candidate),
            });
          }}
        >
          {(candidate ? operatorsFor(candidate.type) : []).map((operator) => (
            <MenuItem key={operator} value={operator}>{t(`Customization_Operator_${operator}`)}</MenuItem>
          ))}
        </TextField>

        {valueEditor(ruleIndex, index, condition, candidate)}

        <Tooltip
          title={rule.when.conditions.length > 1
            ? t('Customization_RemoveCondition')
            : t('Customization_LastCondition')}
        >
          {/* A span so the tooltip still fires while the button is disabled. */}
          <Box component="span" sx={{ pt: 0.5 }}>
            <IconButton
              size="small"
              sx={dangerHoverStyles}
              disabled={disabled || rule.when.conditions.length < 2}
              onClick={removeCondition}
            >
              <TrashIcon />
            </IconButton>
          </Box>
        </Tooltip>
      </Stack>
    );
  };

  const ruleCard = (rule: PoiFieldRule, ruleIndex: number) => {
    const broken = rule.when.conditions.filter((condition) => !byId.has(condition.fieldId));
    const asksStatus = rule.when.conditions.some((condition) => condition.fieldId === STEP_STATUS_ID);
    const inert = targets.every((target) => {
      const value = rule.then[target];

      return value === undefined || (target === 'visible' && value === baseVisible);
    });

    return (
      <Box key={rule.id ?? ruleIndex} sx={ruleCardStyles}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {t('Customization_RuleWhen')}
          </Typography>
          <TextField
            select
            size="small"
            disabled={disabled}
            value={rule.when.match}
            sx={{ minWidth: 170 }}
            onChange={(event) => setRule(ruleIndex, {
              ...rule, when: { ...rule.when, match: event.target.value as 'all' | 'any' },
            })}
          >
            <MenuItem value="all">{t('Customization_MatchAll')}</MenuItem>
            <MenuItem value="any">{t('Customization_MatchAny')}</MenuItem>
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={t('Customization_DeleteRule')}>
            <IconButton
              size="small"
              sx={dangerHoverStyles}
              disabled={disabled}
              onClick={() => onChange(rules.filter((_, position) => position !== ruleIndex))}
            >
              <TrashIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack spacing={1.5}>
          {rule.when.conditions.map((condition, index) => conditionRow(ruleIndex, condition, index))}
        </Stack>

        <Button
          size="small"
          startIcon={<PlusIcon />}
          sx={{ ...dashedButtonStyles, mt: 1.5 }}
          disabled={disabled || rule.when.conditions.length >= MAX_CONDITIONS}
          onClick={() => setRule(ruleIndex, {
            ...rule,
            when: { ...rule.when, conditions: [...rule.when.conditions, newCondition()] },
          })}
        >
          {t('Customization_AddCondition')}
        </Button>

        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ fontWeight: 700, lineHeight: 1, display: 'block', mt: 2, mb: 1 }}
        >
          {t('Customization_RuleThen')}
        </Typography>

        <Stack spacing={1}>
          {targets.map((target) => (
            <Stack key={target} direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                {t(`Customization_${target === 'visible' ? 'Visible'
                  : target === 'required' ? 'Required'
                    : target === 'readOnly' ? 'ReadOnly' : 'ShowOnReports'}`)}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                disabled={disabled}
                value={stateOf(rule.then, target)}
                onChange={(_, next: ThreeState | null) => {
                  if (!next) return;

                  const then = { ...rule.then };

                  if (next === 'leave') delete then[target];
                  else then[target] = next === 'on';

                  setRule(ruleIndex, { ...rule, then });
                }}
              >
                <ToggleButton value="leave">{t('Customization_RuleLeaveAsIs')}</ToggleButton>
                <ToggleButton value="on">{t('Customization_RuleOn')}</ToggleButton>
                <ToggleButton value="off">{t('Customization_RuleOff')}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          ))}
        </Stack>

        {broken.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {t('Customization_RuleBrokenRef', [broken.map((condition) => condition.fieldId).join(', ')])}
          </Alert>
        )}

        {inert && (
          <Alert severity="info" sx={{ mt: 1.5 }}>{t('Customization_RuleNoEffect')}</Alert>
        )}

        {/* The device answers this one only from the app version that knows it. */}
        {asksStatus && (
          <Alert severity="info" sx={{ mt: 1.5 }}>{t('Customization_StatusConditionNote')}</Alert>
        )}
      </Box>
    );
  };

  return (
    <Stack spacing={2}>
      {rules.map(ruleCard)}

      {rules.length === 0 ? (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {/* Showing something conditionally takes a pair — the base flag off, and a rule
              that turns it on — so the button that offers it sets both. */}
          <Button
            size="small"
            startIcon={<PlusIcon />}
            sx={dashedButtonStyles}
            disabled={disabled}
            onClick={() => { onBaseVisibleChange(false); addRule(true); }}
          >
            {t('Customization_AddShowWhenRule')}
          </Button>
          <Button
            size="small"
            startIcon={<PlusIcon />}
            sx={dashedButtonStyles}
            disabled={disabled}
            onClick={() => addRule(false)}
          >
            {t('Customization_AddHideWhenRule')}
          </Button>
        </Stack>
      ) : (
        <Button
          size="small"
          startIcon={<PlusIcon />}
          sx={dashedButtonStyles}
          disabled={disabled || rules.length >= MAX_RULES}
          onClick={() => addRule(!baseVisible)}
        >
          {t('Customization_AddRule')}
        </Button>
      )}
    </Stack>
  );
};

const labelOfOption = (option: PoiFieldOption, position: number) =>
  Object.values(option.label ?? {}).find((text) => text?.trim()) ?? `#${position + 1}`;

const nameOfOption = (options: PoiFieldOption[], id: string) => {
  const found = options.findIndex((option) => option.id === id);

  return found < 0 ? id : labelOfOption(options[found], found);
};

/**
 * The field picker's contents, subheadered by where each field is defined — the type's own
 * first, then one block per ancestor, matching the order the schema table draws them in.
 */
const pickerItems = (candidates: RuleCandidate[], ownLabel: string) => {
  const sources = [...new Set(candidates.map((candidate) => candidate.source))];

  return sources.flatMap((source) => [
    <ListSubheader key={`h-${source ?? 'own'}`}>{source ?? ownLabel}</ListSubheader>,
    ...candidates
      .filter((candidate) => candidate.source === source)
      .map((candidate) => (
        <MenuItem key={candidate.id} value={candidate.id}>
          {candidate.groupName ? `${candidate.groupName} · ${candidate.label}` : candidate.label}
        </MenuItem>
      )),
  ]);
};

export default RuleEditor;
